import { Button, StarRating } from '@app/components';
import MessageIcon from '@app/assets/icons/message.svg?react';
import AutomationIcon from '@app/assets/icons/automation-button.svg?react';
import UsersIcon from '@app/assets/icons/grid/users.svg?react';
import OpenEnvelopeIcon from '@app/assets/icons/grid/envelope.svg?react';
import DashboardStarsIcon from '@app/assets/images/dashboard-stars.svg?react';
import GoogleIcon from '@app/assets/icons/Google.svg?react';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import BoxArrowIcon from '@app/assets/icons/box_arrow.svg?react';
import PlusIcon from '@app/assets/icons/plus.svg?react';
import { DashboardReviewItem } from './DashboardReviewItem';
import { DashboardFollowUpItem } from './DashboardFollowUpItem';
import { useDashboardHook } from './Dashboard.hooks';
import { useNavigate } from 'react-router-dom';
import { IntegrationLabels, IntegrationType, planNames } from '@app/utils/consts';
import { getBusinessPlanType } from '@app/utils/utils';
import { useEffect, useState } from 'react';
import { getUserWithBusinesses } from '@app/services/user';
import {DashboardPeriodMetric} from "@app/pages/Dashboard/DashboardPeriodMetric.tsx";
import {ExternalReview} from "@app/models";

export const Dashboard = () => {
  const {
    businessState,
    integrationsData,
    integrationsLoading,
    dashboardMetrics,
    dashboardMetricsLoading,
    futureSchedules,
    schedulesLoading,
    onboardingData,
    onboardingLoading,
    nextPendingStep,
    handleStepAction,
    isCompletingStep,
  } = useDashboardHook();
  const navigate = useNavigate();
  const currentBusinessId = businessState?.selected?.id;
  const [planType, setPlanType] = useState<string | null>(
    currentBusinessId ? getBusinessPlanType(currentBusinessId) : null
  );
  const currentPlanInfo = planType
    ? planNames.find((p) => p.name === planType)
    : undefined;
  const displayPlanLabel = planType === 'BASIC' ? 'STARTER' : planType || '';

  // Refresh user plan types when arriving to Dashboard (user may have changed plan)
  useEffect(() => {
    if (!currentBusinessId) return;
    let isMounted = true;
    getUserWithBusinesses()
      .then((res) => {
        const subs = res?.data?.user?.subscriptions;
        if (!subs) return;
        const planTypes = subs.map((subscription: any) => ({
          businessId: subscription.businessId,
          planType: subscription.plan.type,
        }));
        localStorage.setItem('userPlanTypes', JSON.stringify(planTypes));
        if (isMounted) {
          const newType = planTypes.find(
            (p: any) => p.businessId === currentBusinessId
          )?.planType;
          setPlanType(newType ?? null);
        }
      })
      .catch(() => {
        // Ignore errors; fallback to existing localStorage value
      });
    return () => {
      isMounted = false;
    };
  }, [currentBusinessId]);

  // Check if there are any integrations
  const hasIntegrations =
    integrationsData?.integrations && integrationsData.integrations.length > 0;

  // Use combined metrics from API
  const totalReviews = dashboardMetrics?.reviewsCount || 0;
  const averageRating = dashboardMetrics?.averageStarRating || 0;

  const businessInfoInOneCol = dashboardMetrics?.byIntegration?.length > 1;
  const businessInfo1 = (<>
    <div className="flex gap-2 p-3 sm:p-4">
      <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
        <MessageIcon width={20} height={20} className="sm:w-6 sm:h-6" />
      </div>
      <div className="flex-1">
        <span className="font-bold text-lg sm:text-xl">{totalReviews}</span>
        <p className="text-base-coolGray text-xs sm:text-sm">Reviews</p>
      </div>
      {averageRating > 0 && (
        <div className="flex items-center gap-2 self-start px-3 py-2 rounded-lg" style={{ backgroundColor: '#F8F9FB' }}>
          <span className="text-base sm:text-lg font-semibold">
            {averageRating.toFixed(1)}
          </span>
          <StarRating rating={averageRating} size={{ width: 16, height: 16 }}/>
        </div>
      )}
    </div>
    <hr />
    <div className="flex gap-2 p-3 sm:p-4">
      <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
        <UsersIcon width={20} height={20} className="sm:w-6 sm:h-6" />
      </div>
      <div className="flex-1">
        <span className="font-bold text-lg sm:text-xl">
          {dashboardMetrics?.customersCount || 0}
        </span>
        <p className="text-base-coolGray text-xs sm:text-sm">Customers</p>
      </div>
      <button
        onClick={() => navigate('/customers/contacts?create=true')}
        className="flex items-center justify-center hover:opacity-80 transition-opacity"
      >
        <PlusIcon className="w-6 h-6" />
      </button>
    </div>
  </>);
  const businessInfo2 = (<>
    <div className="flex gap-2 p-3 sm:p-4">
      <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
        <OpenEnvelopeIcon width={20} height={20} className="sm:w-6 sm:h-6" />
      </div>
      <div className="flex-1">
        <span className="font-bold text-lg sm:text-xl">
          {dashboardMetrics?.campaignsCount || 0}
        </span>
        <p className="text-base-coolGray text-xs sm:text-sm">Campaigns sent</p>
      </div>
      <button
        onClick={() => navigate('/campaigns/create/settings')}
        className="flex items-center justify-center hover:opacity-80 transition-opacity"
      >
        <PlusIcon className="w-6 h-6" />
      </button>
    </div>
    <hr />
    <div className="flex gap-2 p-3 sm:p-4">
      <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
        <AutomationIcon className="[&_path]:fill-base-davyGray sm:w-5 sm:h-5" />
      </div>
      <div className="flex-1">
        <span className="font-bold text-lg sm:text-xl">
          {dashboardMetrics?.automationsCount || 0}
        </span>
        <p className="text-base-coolGray text-xs sm:text-sm">Automations</p>
      </div>
      <button
        onClick={() => navigate('/automations/create/settings')}
        className="flex items-center justify-center hover:opacity-80 transition-opacity"
      >
        <PlusIcon className="w-6 h-6" />
      </button>
    </div>
  </>);

  return (
    <div>
      <div className="bg-white border-b border-b-base-ghostWhite">
        <div className="container mx-auto p-3 sm:p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-bold text-xl sm:text-2xl lg:text-[28px] truncate">
              {businessState?.selected?.name}
            </h2>
            {currentPlanInfo && (
              <span
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 sm:px-5 py-1.5 sm:py-2 rounded-md bg-gradient-to-r from-[#52B6FE] to-[#4ADA73] ring-1 ring-white/70 shadow-sm ml-2"
              >
                {currentPlanInfo.Icon && (
                  <currentPlanInfo.Icon className="w-4 h-4 sm:w-5 sm:h-5 [&_*]:stroke-white [&_*]:fill-transparent" />
                )}
                <span className="uppercase tracking-wide">
                  {displayPlanLabel}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="container mx-auto p-2 sm:p-4">
        <div className="bg-white">
          <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h4 className="font-bold">Onboarding</h4>
              <span className="text-xs text-gray-500">
                {onboardingLoading ? '...' : `${onboardingData?.completionPercentage || 0}%`}
              </span>
            </div>
            <Button 
              kind="secondary" 
              className="font-normal w-full sm:w-auto"
              onClick={() => navigate('/onboarding')}
            >
              Open onboarding
            </Button>
          </div>
          <hr />
          {/* Only show step details if there are pending steps or if loading */}
          {(nextPendingStep || onboardingLoading) && (
            nextPendingStep ? (
              <div className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center sm:justify-between">
                <div className="flex-shrink-0 mt-1">
                  <button
                    onClick={() => handleStepAction(nextPendingStep.id)}
                    disabled={isCompletingStep}
                    className="w-6 h-6 border-2 border-gray-300 rounded-full hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Mark as completed"
                  ></button>
                </div>
                <div className="flex-1 sm:mr-auto">
                  <h4 className="font-semibold text-gray-900">
                    {nextPendingStep.name}
                  </h4>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {nextPendingStep.description}
                  </p>
                </div>
                <div className="w-full sm:w-auto">
                  <Button 
                    className="font-semibold w-full sm:w-auto min-w-fit px-4"
                    onClick={() => navigate(nextPendingStep.redirectUrl)}
                  >
                    Open
                  </Button>
                </div>
              </div>
            ) : onboardingLoading && (
              <div className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center sm:justify-between">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                </div>
                <div className="flex-1 sm:mr-auto">
                  <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
                <div className="w-full sm:w-auto">
                  <Button className="font-semibold w-full sm:w-auto min-w-fit px-4" disabled>
                    Loading...
                  </Button>
                </div>
              </div>
            )
          )}
        </div>
        <hr className="my-4 sm:my-6" />

        {!integrationsLoading && !hasIntegrations ? (
          // No integrations state - matching the screenshot
          <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4 bg-white">
            <div className="mb-8 sm:mb-12">
              <DashboardStarsIcon width={96} height={96} className="sm:w-32 sm:h-32" />
            </div>
            <h3 className="font-bold text-lg sm:text-xl text-gray-800 mb-2 text-center">
              No review platforms connected
            </h3>
            <p className="text-base-coolGray text-center mb-4 sm:mb-6 max-w-md text-sm sm:text-base">
              Add integrations with your review platforms to see the data here
            </p>
            <Button 
              onClick={() => navigate('/integrations/reviews')}
              className="bg-blue-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold w-full sm:w-auto"
            >
              Add integration
            </Button>
          </div>
        ) : (
          // Updated dashboard content with review metrics
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left column - Key metrics */}
              <div className="bg-white  order-2 lg:order-1">
                {businessInfo1}
                {businessInfoInOneCol && (
                  <>
                    <hr />
                    {businessInfo2}
                  </>
                )}
              </div>

              {!businessInfoInOneCol && (
                <div className="bg-white  order-2 lg:order-1">
                  {businessInfo2}
                </div>
              )}

              {/* Right column - Platform reviews */}
              <div className={`${businessInfoInOneCol ? 'lg:col-span-2' : 'lg:col-span-1'} order-1 lg:order-2`}>
                {/* Platform cards - side by side */}
                <div className={`grid grid-cols-1 ${businessInfoInOneCol ? 'sm:grid-cols-2' : 'sm:grid-cols-1'} gap-4`}>
                  {dashboardMetricsLoading ? (
                    <div className="bg-white p-3 sm:p-4 rounded-lg border">
                      <div className="animate-pulse">
                        <div className="h-5 sm:h-6 bg-gray-200 rounded mb-2"></div>
                        <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </div>
                  ) : (
                    dashboardMetrics?.byIntegration.map((integration: any) => (
                      <div className="bg-white p-3 sm:p-4 rounded-lg border" key={integration.integration}>
                        <div className="flex items-center gap-2 mb-3">
                          {integration?.integration == IntegrationType.GOOGLE && ( <GoogleIcon width={20} height={20} className="sm:w-6 sm:h-6" /> )}
                          {integration?.integration == IntegrationType.YELP && ( <YelpIcon width={20} height={20} className="sm:w-6 sm:h-6" /> )}
                          <span className="font-semibold text-sm sm:text-base">{ IntegrationLabels[integration?.integration as IntegrationType] }</span>
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl sm:text-2xl font-bold">
                            {integration.averageStarRating.toFixed(2)}
                          </span>
                          <StarRating rating={integration.averageStarRating} size={{ width: 20, height: 20 }}/>
                        </div>
                        <hr className="border-gray-200 mb-2" />
                        <p className="text-xs sm:text-sm text-gray-600">
                          Based on {integration.count} reviews
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Review trends - full width row with top margin */}
            <hr className="border-gray-200 mt-4 sm:mt-6 mb-4 sm:mb-6" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Today */}
              <DashboardPeriodMetric period="today" prevPeriod="day" value={dashboardMetrics?.reviewsToday} change={dashboardMetrics?.reviewsTodayChange} />
              
              {/* This week */}
              <DashboardPeriodMetric period="week" value={dashboardMetrics?.reviewsWeek} change={dashboardMetrics?.reviewsWeekChange} />

              {/* This month */}
              <DashboardPeriodMetric period="month" value={dashboardMetrics?.reviewsMonth} change={dashboardMetrics?.reviewsMonthChange} />

              {/* This year */}
              <DashboardPeriodMetric period="year" value={dashboardMetrics?.reviewsYear} change={dashboardMetrics?.reviewsYearChange} />
            </div>

            {/* Schedules and Reviews cards - full width row with top margin */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 sm:mt-6">
              {/* Upcoming follow ups */}
              <div className="bg-white p-3 sm:p-4 rounded-lg border">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                  <h4 className="font-semibold text-sm">Upcoming follow ups</h4>
                  <button
                    className="px-3 sm:px-5 py-2 sm:py-3 rounded-lg bg-base-ghostWhite text-base-blue disabled:opacity-50 text-xs w-full sm:w-auto"
                    onClick={() => navigate('/campaigns')}
                  >
                    All campaigns
                  </button>
                </div>
                <hr className="border-gray-200 mb-4" />
                {(() => {
                  const schedules = futureSchedules?.schedules || [];

                  if (schedulesLoading) {
                    return (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-pulse">
                          <div className="h-6 bg-gray-200 rounded mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    );
                  }

                  if (schedules.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-8">
                        <div className="bg-gray-100 rounded-lg p-3 mb-3">
                          <BoxArrowIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="font-bold text-gray-600 mb-1">
                          No follow ups
                        </p>
                        <p className="text-xs text-gray-500 text-center">
                          No campaigns that will be sent soon
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="max-h-96 overflow-y-auto">
                      <div className="divide-y divide-gray-100">
                        {schedules
                          .slice(0, 5)
                          .map((schedule: any, index: number) => (
                            <DashboardFollowUpItem
                              key={index}
                              schedule={{
                                start: schedule.start,
                                campaignId: schedule.campaignId,
                                campaignName: schedule.campaignName,
                                customerCount: schedule.customerCount, // This could be fetched from campaign details
                              }}
                              onClick={() =>
                                navigate(`/campaigns/${schedule.campaignId}`)
                              }
                            />
                          ))}
                      </div>
                      {schedules.length > 5 && (
                        <div className="text-center pt-3">
                          <p className="text-xs text-gray-500">
                            +{schedules.length - 5} more schedules
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* New reviews */}
              <div className="bg-white p-3 sm:p-4 rounded-lg border">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                  <h4 className="font-semibold text-sm">New reviews</h4>
                  <button
                    className="px-3 sm:px-5 py-2 sm:py-3 rounded-lg bg-base-ghostWhite text-base-blue disabled:opacity-50 text-xs w-full sm:w-auto"
                    onClick={() => navigate('/ratings/reviews')}
                  >
                    All reviews
                  </button>
                </div>
                <hr className="border-gray-200 mb-4" />
                {(() => {
                  const allReviews = dashboardMetrics?.latestReviews;

                  if (allReviews?.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-8">
                        <div className="bg-gray-100 rounded-lg p-3 mb-3">
                          <BoxArrowIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="font-bold text-gray-600 mb-1">
                          No new reviews
                        </p>
                        <p className="text-xs text-gray-500 text-center">
                          You have not received any new reviews in the last week
                        </p>
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="max-h-96 overflow-y-auto">
                        <div className="divide-y divide-gray-100">
                          {allReviews?.map((review: ExternalReview, index: any) => {

                          return (
                            <DashboardReviewItem
                              key={index}
                              review={review}
                              onClick={() => navigate('/ratings/reviews')}
                            />
                          );
                          })}
                        </div>
                        {dashboardMetrics?.reviewsWeek > 5 && (
                          <div className="text-center pt-3">
                            <p className="text-xs text-gray-500">
                              +{dashboardMetrics?.reviewsWeek - 5} more reviews
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <button
                          onClick={() => navigate('/ratings/reviews')}
                          className="w-full py-3 rounded-lg bg-base-ghostWhite text-base-blue text-sm font-medium hover:bg-gray-100 transition-colors"
                        >
                          All reviews
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
